import { Error } from '@cognitiv/cicada-ui';
import * as Sentry from '@sentry/browser';
import { setModal } from 'ducks/modals/slices';
import { updateSettings } from 'ducks/settings/slices';
import { clearUser } from 'ducks/user/slices';
import { store } from 'store';
import { deleteStorage } from 'utils/storage';

type ErrorBuilder = {
  name: string;
  message: string;
  endpoint: string;
  path: string;
  status: number;
  data: any;
  error: string;
  raw: any;
};

const { REACT_APP_ENVIRONMENT, REACT_APP_VERSION, REACT_APP_SERVICE, REACT_APP_SENTRY_DNS } = process.env;

Sentry.init({
  dsn: REACT_APP_SENTRY_DNS,
  release: `${REACT_APP_SERVICE}@${REACT_APP_VERSION}`,
  environment: REACT_APP_ENVIRONMENT,
});

export const onSuccess = <TData>(
  res: { status?: number; config?: any } = {},
  resolve: (res: { [key: string]: any }) => void,
  reject: (err: { [key: string]: any }) => void,
  mock?: TData,
) => {
  // capture specific status codes to resolve appropriately
  // matches the status of the response to the synthetic status
  if (res?.status === 204) {
    // this is used when we expect an empty array or object
    // 204 status will always return an empty string when no data is received
    if (mock) {
      resolve({ data: mock });
    } else {
      reject(buildErrorMessage(res));
    }
  }
  resolve(res);
};

export const onError = (err: any = {}, reject: (err: { [key: string]: any }) => void) => {
  // capturing errors from the API
  const error = buildErrorMessage(err);

  if (err?.message === 'timeout of 0ms exceeded') {
    error.status = 408;
  }

  if (error.status === 401) {
    error.error = 'Session has Expired';
  }

  if (error.status === 404) {
    error.error = 'Not Found';
  }

  reject(error);
};

export const handleError = async (err: any) => {
  store.dispatch(updateSettings({ loading: false }));

  if (err.status === 401 || err.error === 'HashMismatch') {
    store.dispatch(clearUser());
    deleteStorage();
    store.dispatch(updateSettings({ ...err, loading: false }));
    return err;
  }

  store.dispatch(updateSettings(err));
  store.dispatch(setModal({ api_error: true }));

  if (err.message === 'Network Error') {
    return err;
  }

  const { user } = store.getState();
  Sentry.configureScope((scope) => {
    scope.setLevel(REACT_APP_ENVIRONMENT === 'production' ? 'error' : 'warning');
    scope.setExtra('user.name', user.display_name);
    scope.setExtra('organization', user.organization_name);
    scope.setExtra('path', err.path);
    scope.setExtra('endpoint', err.endpoint);
    scope.setExtra('message', err.message);
    scope.setExtra('error', err.error);
    scope.setExtra('status', err.status);
    scope.setExtra('data', err.data);
    scope.setTag('service', REACT_APP_SERVICE);
    scope.setTag('name', `${err.status}: ${err.name}`);
    Sentry.captureException(err.raw);
  });

  return err;
};

export const buildErrorMessage = (res: any): ErrorBuilder => {
  const message = '204: Please contact Cognitiv.';

  return {
    name: res?.name || 'No Content',
    message: res?.status === 204 ? message : res?.message,
    endpoint: res?.config?.url || null,
    path: window.location.pathname,
    status: res?.status || res?.response?.status,
    data: res?.response?.data || null,
    error: captureError(res),
    raw: res || null,
  };
};

const captureError = (res: any): string => {
  if (res?.status === 204) {
    return 'Error: If the error persists please contact Cognitiv.';
  }
  if (res?.response?.data?.error) {
    return res?.response?.data?.error;
  }
  if (res?.response?.data) {
    const result = Object.entries(res.response.data)
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n');
    return result;
  }
  if (res?.data) {
    return JSON.stringify(res?.data);
  }
  return 'Error: If the error persists please contact Cognitiv.';
};

export const getValidationError = <T>({ errors, key }: { errors: Error[]; key: keyof T }) => {
  const error = errors.find((e) => e.key === key);

  return !!error;
};
